import { Injectable, inject } from '@angular/core';
import { ApiQueryParams, ApiService } from '@app/gateway';
import { BackupFactory, BackupFile, BackupModel } from '../models';
import { BackupRepository } from '../repository';
import { BaseService } from './base';

export interface BackupsPaginatedResult {
  data: BackupModel[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface BackupsFilters {
  fantasia?: string;
  ativo?: boolean;
}

@Injectable({ providedIn: 'root' })
export class BackupsService extends BaseService<BackupModel> {
  private readonly apiService = inject(ApiService);

  constructor(protected override readonly repository: BackupRepository) {
    super(repository);
  }

  public async getAllBackups(): Promise<BackupModel[]> {
    try {
      const path: string = `${BackupFactory.getPath()}`;
      const backups: BackupModel[] = await this.apiService.get({ path });
      return backups;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async getAllBackupsPaginated(params: { page: number; limit: number } & BackupsFilters): Promise<BackupsPaginatedResult> {
    try {
      const path: string = `${BackupFactory.getPath()}/paginated`;
      const query: ApiQueryParams = { page: params.page, limit: params.limit };
      if (params.fantasia) query['fantasia'] = params.fantasia;
      if (params.ativo !== undefined) query['ativo'] = params.ativo;
      const response = await this.apiService.get<BackupsPaginatedResult | BackupModel[]>({ path, query });
      if (Array.isArray(response)) {
        return this.buildPaginatedFromArray(response, params);
      }
      return response;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  private buildPaginatedFromArray(all: BackupModel[], params: { page: number; limit: number } & BackupsFilters): BackupsPaginatedResult {
    let data = all;
    if (params.ativo !== undefined) {
      data = data.filter((b) => b.account?.backup === params.ativo);
    }
    if (params.fantasia) {
      const term = params.fantasia.toLowerCase();
      data = data.filter((b) => b.account?.fantasia?.toLowerCase().includes(term) || b.account?.accountId?.toLowerCase().includes(term));
    }
    const total = data.length;
    const totalPages = Math.ceil(total / params.limit) || 1;
    const start = (params.page - 1) * params.limit;
    return {
      data: data.slice(start, start + params.limit),
      total,
      page: params.page,
      limit: params.limit,
      totalPages,
      hasNext: params.page < totalPages,
      hasPrev: params.page > 1,
    };
  }

  public async backupSchedule(id: string, schedule: string): Promise<void> {
    try {
      const data = { schedule };
      const path: string = `${BackupFactory.getPath()}/${id}/schedule`;
      await this.apiService.post({ path, data });
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async listBackupFiles(accountId: string): Promise<BackupFile[]> {
    try {
      const path: string = `${BackupFactory.getPath()}/${accountId}/files`;
      const files: BackupFile[] = await this.apiService.get({ path });
      return files;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async backupDownloadFile(accountId: string, fileName: string): Promise<string> {
    try {
      const path: string = `${BackupFactory.getPath()}/${accountId}/download/${fileName}`;
      const response: { url: string } = await this.apiService.get({ path });
      return response.url;
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async executeBackup(accountId: string): Promise<void> {
    try {
      const path: string = `${BackupFactory.getPath()}/execute/${accountId}`;
      await this.apiService.post({ path });
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async deleteBackupFile(accountId: string, fileName: string): Promise<void> {
    try {
      const path: string = `${BackupFactory.getPath()}/${accountId}/file/${fileName}`;
      await this.apiService.delete({ path });
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }
}
